import { Controller } from "@hotwired/stimulus"

// Live preview for the background-removal upload field.
//
// The removal itself is done server-side by RMagick, so the preview posts the
// chosen file (or asks for the stored one) to a preview endpoint and shows the
// PNG that comes back — what you see is exactly what will be saved.
export default class extends Controller {
    static targets = ["file", "toggle", "options", "tolerance", "toleranceLabel",
                      "original", "result", "resultWrap", "spinner", "caption",
                      "placeholder", "apply", "applyLabel"]
    static values = { url: String, applyUrl: String, recordId: Number }

    static DEBOUNCE_MS = 250

    connect() {
        this.refresh()
    }

    disconnect() {
        clearTimeout(this.timer)
        this.releasePreview()
        this.controller?.abort()
    }

    // Checkbox changed.
    refresh() {
        const on = this.toggleTarget.checked
        this.optionsTarget.hidden = !on
        this.resultWrapTarget.hidden = !on

        if (on) this.schedulePreview()
    }

    toleranceMoved() {
        const value = this.toleranceTarget.value
        if (this.hasToleranceLabelTarget) this.toleranceLabelTarget.textContent = value

        this.schedulePreview()
    }

    fileChanged() {
        this.showLocalOriginal()
        if (this.toggleTarget.checked) this.schedulePreview()
    }

    // Show the newly picked file as the "before" image straight away.
    showLocalOriginal() {
        const file = this.fileTarget.files?.[0]
        if (!file) return

        this.releaseOriginal()
        this.originalObjectUrl = URL.createObjectURL(file)
        this.originalTarget.src = this.originalObjectUrl
        this.originalTarget.hidden = false
        if (this.hasPlaceholderTarget) this.placeholderTarget.hidden = true
    }

    // Dragging the slider fires constantly; only the last position is worth a request.
    schedulePreview() {
        clearTimeout(this.timer)
        this.timer = setTimeout(() => this.preview(), this.constructor.DEBOUNCE_MS)
    }

    async preview() {
        const file = this.fileTarget.files?.[0]
        // Nothing chosen and nothing stored — there is nothing to preview.
        if (!file && !this.recordIdValue) return

        const body = new FormData()
        if (file) body.append("file", file)
        // No file picked: the endpoint reprocesses the stored image for this record.
        else if (this.recordIdValue) body.append("id", this.recordIdValue)
        body.append("tolerance", this.toleranceTarget.value)
        body.append("authenticity_token", this.token)

        this.controller?.abort()
        this.controller = new AbortController()
        this.setBusy(true)

        try {
            const response = await fetch(this.urlValue, {
                method: "POST", body, signal: this.controller.signal,
                headers: { Accept: "image/png, application/json" },
            })

            if (!response.ok) {
                const message = await response.json().catch(() => ({}))
                return this.showMessage(message.error || "Vorschau fehlgeschlagen.")
            }

            const blob = await response.blob()
            this.releasePreview()
            this.previewObjectUrl = URL.createObjectURL(blob)
            this.resultTarget.src = this.previewObjectUrl
            this.resultTarget.hidden = false
            this.captionTarget.textContent = "Vorschau"
        } catch (error) {
            if (error.name !== "AbortError") this.showMessage("Vorschau fehlgeschlagen.")
        } finally {
            this.setBusy(false)
        }
    }

    async applyToStored() {
        if (!this.applyUrlValue) return

        const label = this.applyLabelTarget
        const previous = label.textContent
        label.textContent = "Wird angewendet…"
        this.applyTarget.disabled = true

        try {
            const body = new FormData()
            body.append("background_tolerance_input", this.toleranceTarget.value)
            body.append("authenticity_token", this.token)

            const response = await fetch(this.applyUrlValue, {
                method: "POST", body, headers: { Accept: "application/json" },
            })
            const result = await response.json().catch(() => ({}))

            if (!response.ok) {
                label.textContent = result.error || "Fehlgeschlagen."
                return
            }

            // Show the saved result as the new original; the cache-buster stops
            // the browser serving the pre-processing image.
            this.originalTarget.src = `${result.url}${result.url.includes("?") ? "&" : "?"}v=${Date.now()}`
            this.originalTarget.hidden = false
            label.textContent = "Angewendet"
        } catch {
            label.textContent = "Fehlgeschlagen."
        } finally {
            this.applyTarget.disabled = false
            setTimeout(() => { label.textContent = previous }, 2500)
        }
    }

    get token() {
        return document.querySelector("meta[name='csrf-token']")?.content || ""
    }

    showMessage(text) {
        this.captionTarget.textContent = text
        this.resultTarget.hidden = true
    }

    setBusy(busy) {
        if (this.hasSpinnerTarget) this.spinnerTarget.hidden = !busy
    }

    releasePreview() {
        if (this.previewObjectUrl) URL.revokeObjectURL(this.previewObjectUrl)
        this.previewObjectUrl = null
    }

    releaseOriginal() {
        if (this.originalObjectUrl) URL.revokeObjectURL(this.originalObjectUrl)
        this.originalObjectUrl = null
    }
}
